import { messageActions } from "reusable/mui/modals/message-actions"
import { messageInfo } from "reusable/mui/modals/message-info"
import { CustomTable } from "reusable/mui/table"
import { TrpcFetcherOptions, TrpcResponse, trpcResponseSchema } from "@gap/backend/endpoints/trpc/trpc-types"
import { orgContextCache } from "cache/orgContextCache"
import Box from "@mui/material/Box"
import { themeConfig } from '@gap/shared/theme'
import { DialogButtonOptions } from "reusable/gap-dialog/GapDialogActionButton"
import * as Sentry from "@sentry/browser"
import variables from "variables/variables"
import { GapError } from "@trpc-endpoints/trpc/error-handler/GapError"
import { forceLogoutWithReason } from "auth/logout-reason"
import { isProdLikeStage } from "@gap/utils/stage"

let messagesOnDisplay: string[] = []

const displayMessage = async (error: ErrorTempType) => {
   const { actions, content, title } = error

   const key = `${error.title}-${error.content}`

   if (messagesOnDisplay.includes(key)) return

   messagesOnDisplay.push(key)
   await messageActions({ ...actions, children: content, title })
   messagesOnDisplay = messagesOnDisplay.filter(m => m !== key)
}

export async function trpcFetcher({ fetchParams, auth, cors, silent }: TrpcFetcherOptions): ReturnType<typeof window.fetch> {
   const url = fetchParams[0]
   const options = fetchParams[1] || { headers: {} } as RequestInit

   const headers = options.headers as HeadersInit & { authorization: string; orgid: string | null; 'x-time-zone'?: string }

   if (cors) {
      options.mode = 'cors'
   }

   // attaches auth header when needed
   if (auth?.type === 'jwt') {
      headers['authorization'] = `Bearer ${auth.token}`
      headers['orgid'] = orgContextCache.authOrgId.get() || null
      try {
         headers['x-time-zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch {
         /* ignore */
      }
   }

   const startTime = Date.now()
   let response: any
   try {
      response = await window.fetch(url, options)
   } catch (error: any) {
      Sentry.captureException(error, {
         extra: {
            context: error.message,
            url,
            orgId: orgContextCache.authOrgId.get() || null
         }
      })
      if (!silent) {
         displayMessage({
            title: 'Network request failed',
            content: 'Please reload the page and try again',
            actions: {
               primary: {
                  name: "Reload",
                  notCloseAutomatically: true,
                  callback: () => { window.location.reload() }
               },
               cancel: {
                  name: 'Close',
               }
            }
         })
      }
      throw error
   }

   const endTime = Date.now()
   const duration = endTime - startTime

   // A 401 while gap tokens are present means the guest token was rejected by the API Gateway
   // JWT authorizer. The session cannot recover (the refresh endpoint sits behind the same
   // authorizer), so log out instead of letting polling loops retry forever. Checked before the
   // silent early-return on purpose: session death is global, not a per-call concern.
   if (response.status === 401 && localStorage.getItem(variables.gapAccessTokenLocalStorageKey)) {
      await forceLogoutWithReason({ reason: 'guestSessionExpired' })
      return response
   }

   if (silent) {
      return response
   }

   if (!response.ok || response.status === 207) {
      // we are cloning the response so we can read it twice. `response.json()` is a one-time use function
      const cloned = response.clone()
      const json = await cloned.json()
      const errors: ErrorTempType[] = []

      let parsedBody: TrpcResponse = []

      const parsedResponse = trpcResponseSchema.safeParse(json)

      if (!parsedResponse.success) {
         if (json?.error?.json?.message === GapError.messages.auth.loginExpired) {
            await forceLogoutWithReason({ reason: 'sessionExpired' })
         } else {
            // TODO: remove this once we have a proper error handling in the frontend
            Sentry.captureException('Unexpected trpc response', {
               extra: {
                  context: duration > 28000 ? 'Trpc network timeout' : 'Trpc unexpected network response',
                  url,
                  json,
                  duration: Math.ceil(duration / 1000),
                  orgId: orgContextCache.authOrgId.get() || null,
                  jsonString: JSON.stringify(json),
               }
            })
            errors.push({
               title: 'Operation timed out',
               content: 'Please reload the page and try again',
               actions: {
                  primary: {
                     name: "Reload",
                     notCloseAutomatically: true,
                     callback: () => { window.location.reload() }
                  },
                  cancel: {
                     name: 'Close',
                  }
               }
            })
         }
      } else {
         parsedBody = parsedResponse.data
      }

      for (const responseItem of parsedBody) {
         if (responseItem.error) {
            const { json: { data, message, cause } } = responseItem.error

            const error: ErrorTempType = {
               content: message,
               title: "Unexpected error",
               actions: {}
            }

            switch (data.gapError?.gapErrorData.gapErrorType) {
               case 'triedUpdatingStaleRecord':
                  // if there's any stale record, we need to reload the page
                  error.actions.primary = {
                     name: "Refresh",
                     notCloseAutomatically: true,
                     callback: () => { window.location.reload() }
                  }
                  error.title = 'Reload Required'
                  break;
               case 'support':
                  error.title = 'Contact Support'
                  break
               case 'forbidden':
                  error.title = 'Forbidden'
                  break
               case 'generic':
                  error.title = 'Gap Error'
                  break
               case 'userIsDeactivated': {
                  return response
                  break
               }
            }

            if (!data.gapError) {
               if (isProdLikeStage(variables.stage)) {
                  error.actions.primary = {
                     name: 'Refresh',
                     notCloseAutomatically: true,
                     callback: () => { window.location.reload() }
                  }
                  error.actions.cancel = {
                     name: 'Close',
                  }
               } else {
                  if (!!responseItem.error) {
                     error.actions.primary = {
                        name: 'Full Error',
                        notCloseAutomatically: true,
                        callback: () =>
                           messageInfo(<Box sx={{ fontWeight: 400, color: themeConfig.primary[900], p: '5px' }}>
                              <pre style={{ borderRadius: '8px' }}>
                                 {JSON.stringify(responseItem.error, null, 2)}
                              </pre>
                           </Box>, 'Full Error', {
                              primary: {
                                 name: 'Close'
                              },
                              size: "large"
                           },),
                     }
                  }
                  const stackErrorAction = {
                     name: 'Stack Trace',
                     notCloseAutomatically: true,
                     callback: () => messageInfo(<code>{cause?.stack}</code>, 'Stack Trace', {
                        primary: {
                           name: 'Close'
                        },
                        size: 'large'
                     }),
                  }
                  if (error.actions.primary) {
                     error.actions.secondary = stackErrorAction
                  } else {
                     error.actions.primary = stackErrorAction
                  }
               }
            }

            if (!errors.find(e => e.content === error.content && e.title === error.title)) {
               errors.push(error)
            }
         }
      }

      if (errors.length === 1) {
         displayMessage(errors[0])
      } else if (errors.length > 1) {
         if (errors.length >= 4) {
            messageActions({
               DialogProps: { maxWidth: 'lg', fullWidth: true },
               primary: {
                  name: 'Ok',
               },
               children: <CustomTable
                  overrides={{
                     toolbar: () => <></>
                  }}
                  rows={errors}
                  columns={[
                     { field: 'title', title: 'Title' },
                     { field: 'content', title: 'Message' },
                  ]}
                  actions={{
                     rowActions: r => Object.values(r.actions).map(a => ({
                        title: a.name as string,
                        callback: a.callback as () => void,
                     }))
                  }}
               />,
               title: 'Errors',
            })
         } else {
            for (const error of errors) {
               await displayMessage(error)
            }
         }
      }
   }

   return response
}

type ErrorTempType = {
   content: string
   title: string
   actions: DialogButtonOptions
}
